import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useChangeMyPassword, useMyProfile, useUpdateMyProfile } from '../api/hooks';
import { apiUpload } from '../api/client';
import { useT } from '../i18n/LangProvider';
import { Pill } from '../ui/atoms';
import { toast } from '../ui/toast';
import { Icon } from '../ui/Icon';

function PasswordCard() {
  const t = useT();
  const change = useChangeMyPassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const canSubmit = current && next.length >= 8 && next === confirm && !change.isPending;

  async function submit() {
    setMsg(null);
    if (next !== confirm) { setMsg({ ok: false, text: t('Le due nuove password non coincidono.') }); return; }
    try {
      await change.mutateAsync({ currentPassword: current, newPassword: next });
      setCurrent(''); setNext(''); setConfirm('');
      setMsg({ ok: true, text: t('Password aggiornata.') });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : t('Modifica non riuscita.') });
    }
  }

  return (
    <div className="card" style={{ padding: 20, marginTop: 16 }}>
      <h2 className="h2" style={{ fontSize: 16, marginBottom: 6 }}>{t('Cambia password')}</h2>
      <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>{t('Minimo 8 caratteri. Le altre sessioni verranno disconnesse.')}</p>
      {msg && (
        <div style={{ borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 13, background: msg.ok ? 'var(--ok-soft)' : 'var(--danger-soft)', color: msg.ok ? 'var(--ok)' : 'var(--danger)', border: `1px solid ${msg.ok ? 'var(--ok)' : 'var(--danger)'}` }}>
          <Icon name={msg.ok ? 'check' : 'warn'} size={13} /> {msg.text}
        </div>
      )}
      <div className="col" style={{ gap: 10, maxWidth: 360 }}>
        <div><label className="field-label">{t('Password attuale')}</label><input className="input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" /></div>
        <div><label className="field-label">{t('Nuova password')}</label><input className="input" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" /></div>
        <div><label className="field-label">{t('Conferma nuova password')}</label><input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></div>
        {next && next.length < 8 && <div className="muted" style={{ fontSize: 12 }}>{t('Almeno 8 caratteri.')}</div>}
        <button className="btn btn--brand" disabled={!canSubmit} onClick={() => void submit()} style={{ alignSelf: 'flex-start' }}>
          {change.isPending ? t('Aggiorno…') : t('Aggiorna password')}
        </button>
      </div>
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = { admin: 'Amministratore', 'team-lead': 'Team Lead', 'customer-care': 'Customer Care', user: 'Utente' };
const initialsOf = (name: string, email: string) => {
  const base = (name || email).trim();
  const parts = base.split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || base.slice(0, 2).toUpperCase();
};

export function ProfileScreen() {
  const t = useT();
  const auth = useAuth();
  const profileQ = useMyProfile();
  const update = useUpdateMyProfile();
  const fileRef = useRef<HTMLInputElement>(null);

  const [nickname, setNickname] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const me = profileQ.data;
  const currentNick = me?.nickname ?? '';
  const draftNick = nickname ?? currentNick;
  const dirty = draftNick.trim() !== currentNick.trim();
  const avatar = me?.avatarUrl ?? null;
  const displayName = me?.name || auth.name || auth.email;

  useEffect(() => { if (savedMsg) { const to = setTimeout(() => setSavedMsg(null), 2000); return () => clearTimeout(to); } }, [savedMsg]);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await apiUpload(file);
      await update.mutateAsync({ avatarUrl: url });
      setSavedMsg(t('Immagine aggiornata'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('Caricamento non riuscito'));
    } finally {
      setUploading(false);
    }
  }
  async function removeAvatar() {
    await update.mutateAsync({ avatarUrl: '' });
    setSavedMsg(t('Immagine rimossa'));
  }
  async function saveNick() {
    await update.mutateAsync({ nickname: draftNick.trim() });
    setNickname(null);
    setSavedMsg(t('Nickname salvato'));
  }

  return (
    <div className="profile-screen" style={{ padding: 24, maxWidth: 640 }}>
      {/* Hero card with avatar */}
      <div className="card profile-hero">
        <div className="profile-hero__cover" />
        <div className="profile-hero__body">
          <button
            type="button"
            className="profile-avatar"
            onClick={() => fileRef.current?.click()}
            title={t('Cambia immagine')}
            aria-label={t('Cambia immagine profilo')}
            disabled={uploading}
          >
            {avatar
              ? <img src={avatar} alt="" />
              : <span className="profile-avatar__initials">{initialsOf(displayName, me?.email ?? '')}</span>}
            <span className="profile-avatar__edit"><Icon name={uploading ? 'refresh' : 'edit'} size={14} /></span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => void onFile(e.target.files?.[0])} />
          <div className="profile-hero__main" style={{ minWidth: 0, flex: 1 }}>
            <h1 className="h1" style={{ fontSize: 22, margin: 0 }}>{displayName}</h1>
            <div className="muted" style={{ fontSize: 13.5 }}>{me?.email ?? auth.email}{me?.role ? ` · ${me.role}` : ''}</div>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {(me?.roles ?? auth.roles).map((r) => <Pill key={r} tone="orange">{t(ROLE_LABEL[r] ?? r)}</Pill>)}
            </div>
          </div>
          <div className="col profile-hero__actions" style={{ gap: 6, alignItems: 'flex-end' }}>
            <button className="btn btn--sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <Icon name="download" size={12} /> {uploading ? t('Carico…') : t('Cambia foto')}
            </button>
            {avatar && <button className="btn btn--sm" onClick={() => void removeAvatar()}>{t('Rimuovi')}</button>}
          </div>
        </div>
      </div>

      {/* Nickname */}
      <div className="card" style={{ padding: 20, marginTop: 16 }}>
        <h2 className="h2" style={{ fontSize: 16, marginBottom: 6 }}>{t('Come vuoi essere chiamato')}</h2>
        <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
          {t('Il nickname è')} <strong>{t('facoltativo')}</strong> {t('e compare solo nel tuo Hub (“Buongiorno, …”) al posto del nome. Nessun altro lo vede.')}
        </p>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <input className="input" placeholder={displayName.split(' ')[0]} value={draftNick} maxLength={40}
            onChange={(e) => setNickname(e.target.value)} style={{ flex: 1 }} />
          <button className="btn btn--brand" disabled={!dirty || update.isPending} onClick={() => void saveNick()}>
            {update.isPending ? t('Salvo…') : t('Salva')}
          </button>
        </div>
        <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          {draftNick.trim() ? t('Anteprima: “Buongiorno, {nick}.”', { nick: draftNick.trim() }) : t('Lascia vuoto per usare il tuo nome.')}
        </div>
      </div>

      {/* Change password */}
      <PasswordCard />

      {/* Account */}
      <div className="card" style={{ padding: 20, marginTop: 16 }}>
        <h2 className="h2" style={{ fontSize: 16, marginBottom: 8 }}>Account</h2>
        <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
          {t('Account interno Touristation.')}
        </p>
        <button className="btn" onClick={auth.logout}>{t('Esci')}</button>
      </div>

      {savedMsg && <div className="profile-toast" role="status" aria-live="polite"><Icon name="check" size={13} /> {savedMsg}</div>}
    </div>
  );
}
